import { Component, OnDestroy, OnInit } from '@angular/core';
import { ConfirmEventType, ConfirmationService, MessageService } from 'primeng/api';
import { GetBotResponse } from 'src/app/models/bot.interface';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserInterface } from 'src/app/models/user.interface';
import { Workspace } from 'src/app/models/workspace.interface';
import { BotService } from 'src/app/core/services/bot.service';
import { UserService } from 'src/app/core/services/user.service';
import { WorkspaceService } from 'src/app/core/services/workspace.service';
import {
  UsageCounterService,
  ChannelUsageEntry,
} from 'src/app/core/services/usage-counter.service';

@Component({
  selector: 'globot-subscription-plan',
  templateUrl: './subscription-plan.component.html',
  styleUrls: ['./subscription-plan.component.scss'],
})
export class SubscriptionPlanComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  bots: GetBotResponse[] = [];
  user: UserInterface | undefined = undefined;
  /** Workspace activo en el selector del sidebar (puede no ser el mío). Solo se usa para saber si coincide con `ownedWorkspace`. */
  currentWorkspace: Workspace | null = null;
  /** Mi propio workspace (del que soy owner), independiente del que tenga activo en el selector. «Uso» siempre muestra este. */
  ownedWorkspace: Workspace | null = null;
  consumedMessages: number | undefined;
  limitMessages: number | undefined;
  hasPaymentMethod: boolean = false;
  paymentMethodDisplay: string | null = null;
  showPlansDialog: boolean = false;

  /** GLOB-1867 — per-channel queries + leads breakdown for the current cycle. */
  channelUsage: ChannelUsageEntry[] = [];
  isLoadingChannelUsage: boolean = false;
  private channelUsageRequested = false;

  /** Configuración dinámica del modal de confirmación (upgrade / downgrade / cancel). */
  confirmDialogHeader = 'Finalizar suscripción';
  confirmDialogAcceptLabel = 'Sí, finalizar';
  confirmDialogRejectLabel = 'Volver';

  constructor(
    private userService: UserService,
    private botService: BotService,
    private confirmationService: ConfirmationService,
    private readonly ms: MessageService,
    private readonly workspaceService: WorkspaceService,
    private readonly usageCounterService: UsageCounterService
  ) {}

  ngOnInit() {
    this.user = this.userService.getUserLocalStorage();
    this.getMessageCount();

    this.workspaceService
      .getCurrentWorkspaceObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((ws) => {
        this.currentWorkspace = ws;
        this.tryLoadChannelUsage();
      });
    this.workspaceService.getCurrentWorkspace().pipe(takeUntil(this.destroy$)).subscribe();

    this.workspaceService
      .getOwnedWorkspace()
      .pipe(takeUntil(this.destroy$))
      .subscribe((ws) => {
        this.ownedWorkspace = ws;
        this.tryLoadChannelUsage();
      });

    this.userService
      .getUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          this.userService.setUser(user);
          this.user = user;
        },
        error: () => {
          // Fallback a localStorage (ya seteado arriba)
        },
      });

    this.userService
      .getPaymentMethod()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pm) => {
          this.hasPaymentMethod = !!pm?.display;
          this.paymentMethodDisplay = pm?.display ?? null;
        },
        error: () => {
          this.hasPaymentMethod = false;
          this.paymentMethodDisplay = null;
        },
      });
    this.botService
      .getBots()
      .pipe(takeUntil(this.destroy$))
      .subscribe((bots) => {
        this.bots = bots;
        this.tryLoadChannelUsage();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getMessageCount() {
    this.userService
      .getMessageCount()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: any) => {
        this.consumedMessages = response.consumed_messages;
        this.limitMessages = response.limit_messages;
      });
  }

  /** True cuando el workspace activo en el selector es el mismo que poseo (necesario para que el desglose de canales use los bots correctos). */
  get isViewingOwnWorkspace(): boolean {
    return (
      !!this.currentWorkspace &&
      !!this.ownedWorkspace &&
      this.currentWorkspace.id === this.ownedWorkspace.id
    );
  }

  get chatbotsLimit(): number {
    return this.ownedWorkspace?.effective_limits?.chatbots ?? this.user?.subscription_limits?.chatbots ?? 0;
  }

  get sourcesLimit(): number {
    return this.ownedWorkspace?.effective_limits?.sources ?? this.user?.subscription_limits?.sources ?? 0;
  }

  /** Plan de tu cuenta (suscripción personal, facturación). */
  get planAlias(): string | undefined {
    return this.user?.latest_active_subscription?.subscription_type?.alias;
  }

  get planName(): string | undefined {
    return this.user?.latest_active_subscription?.subscription_type?.name;
  }

  get displayMessageConsumed(): number | undefined {
    if (this.ownedWorkspace?.owner_message_quota) {
      return this.ownedWorkspace.owner_message_quota.consumed;
    }
    return this.consumedMessages;
  }

  get displayMessageLimit(): number | undefined {
    if (this.ownedWorkspace?.owner_message_quota) {
      return this.ownedWorkspace.owner_message_quota.limit;
    }
    return this.limitMessages;
  }

  /**
   * GLOB-1867 — Cycle reset date for the message cap.
   *
   * Only defined for the workspace owner, since `owner_message_quota` (the
   * quota_counter-backed rolling-30d cycle) is the only source pinned by the
   * AC for this date. The non-owner fallback (`getMessageCount`) uses a
   * different, calendar-based period and is intentionally not surfaced here.
   */
  get cycleResetDate(): string | undefined {
    return this.ownedWorkspace?.owner_message_quota?.period_end;
  }

  /** Percentage of the cap consumed so far, clamped to [0, 100]. */
  get usagePercent(): number {
    const consumed = this.displayMessageConsumed ?? 0;
    const limit = this.displayMessageLimit ?? 0;
    if (limit <= 0) return 0;
    return Math.min(100, Math.round((consumed / limit) * 100));
  }

  /** Zero-state: no messages consumed yet this cycle. */
  get isZeroUsage(): boolean {
    return (this.displayMessageConsumed ?? 0) === 0;
  }

  /** Approaching the cap (>=90%) but not yet at/over it. */
  get isNearCap(): boolean {
    const consumed = this.displayMessageConsumed ?? 0;
    const limit = this.displayMessageLimit ?? 0;
    if (limit <= 0) return false;
    return consumed < limit && consumed / limit >= 0.9;
  }

  /** At or over the cap. */
  get isAtCap(): boolean {
    const consumed = this.displayMessageConsumed ?? 0;
    const limit = this.displayMessageLimit ?? 0;
    return limit > 0 && consumed >= limit;
  }

  /** Visual severity for the usage progress bar. */
  get usageSeverity(): 'success' | 'warning' | 'danger' {
    if (this.isAtCap) return 'danger';
    if (this.isNearCap) return 'warning';
    return 'success';
  }

  /** True when at least one channel has queries or leads this cycle (zero-state gate). */
  get hasChannelUsageData(): boolean {
    return this.channelUsage.some((c) => c.queries > 0 || c.leads > 0);
  }

  /**
   * GLOB-1867 — Loads the per-channel queries/leads breakdown once both the
   * owner's quota cycle (period_start/period_end) and the workspace's bots
   * are available. Called from both the workspace and bots subscriptions
   * since they resolve independently; guarded so it only fires once.
   */
  private tryLoadChannelUsage(): void {
    if (this.channelUsageRequested) return;
    if (!this.isViewingOwnWorkspace) return;

    const quota = this.ownedWorkspace?.owner_message_quota;
    if (!quota?.period_start || !quota?.period_end) return;
    if (!this.bots.length) return;

    this.channelUsageRequested = true;
    this.isLoadingChannelUsage = true;

    this.usageCounterService
      .getChannelBreakdown(
        this.bots.map((b) => b.id),
        quota.period_start,
        quota.period_end
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (entries) => {
          this.channelUsage = entries;
          this.isLoadingChannelUsage = false;
        },
        error: () => {
          this.channelUsage = [];
          this.isLoadingChannelUsage = false;
        },
      });
  }

  get displayChatbotsCount(): number {
    return this.ownedWorkspace?.chatbots_count ?? this.user?.chatbots_count ?? 0;
  }

  get displayDatasourcesCount(): number {
    return this.ownedWorkspace?.datasources_count ?? this.user?.datasources_count ?? 0;
  }

  get hasActivePaidPeriod(): boolean {
    const latest = this.user?.latest_active_subscription;
    const name = latest?.subscription_type?.name;
    if (!latest || !latest.is_active) return false;
    return name !== 'FREE' && name !== 'FREE-UNLIMIT';
  }

  get isExpiredFree(): boolean {
    const latest = this.user?.latest_active_subscription;
    const name = latest?.subscription_type?.name;
    if (!latest) return false;
    return !latest.is_active && (name === 'FREE' || name === 'FREE-UNLIMIT');
  }

  get showCancelButton(): boolean {
    const name = this.planName;
    return name != null && name !== 'FREE' && name !== 'FREE-UNLIMIT';
  }

  onUpgradeClick(planId: number) {
    this.userService
      .getUpgradePreview(planId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (preview) => this.handleUpgradePreview(planId, preview),
        error: (err) => this.handleUpgradePreviewError(err),
      });
  }

  private handleUpgradePreview(planId: number, preview: any): void {
    const previewData = this.buildUpgradePreviewData(preview);

    if (this.shouldBlockZeroChargeUpgrade(previewData.amount, previewData.zeroChargeUsed)) {
      this.showMessage(
        'info',
        'Un upgrade sin cobro por periodo',
        'Ya utilizaste el upgrade sin cobro de este periodo. El cambio de plan se aplicará en la próxima renovación.'
      );
      return;
    }

    this.configureUpgradeDialog(previewData.isZeroCharge);
    this.confirmationService.confirm({
      message: previewData.message,
      icon: 'pi pi-credit-card',
      accept: () => this.executeUpgrade(planId, previewData.isZeroCharge),
      reject: (type: ConfirmEventType) => this.closeConfirmationOnReject(type),
    });
  }

  private buildUpgradePreviewData(preview: any) {
    const amount = Number(preview.amount_to_charge);
    const currency = preview.currency || 'CLP';
    const days = preview.days_remaining;
    const planName = preview.new_plan_name || 'nuevo plan';
    const nextDate = this.formatDateOrEmpty(preview.next_billing_date);
    const zeroChargeUsed = !!preview.zero_charge_upgrade_used;
    const isZeroCharge = amount === 0;
    const amountFormatted = `${currency} $${amount.toLocaleString('es-CL')}`;
    const message = isZeroCharge
      ? `El cambio de plan se aplicará ahora sin cobro. Tu próxima facturación será el ${nextDate}. ¿Confirmar?`
      : `Por ${days} días restantes se te cobrará <strong>${amountFormatted}</strong> por actualizar a ${planName}. Tu próxima facturación será el ${nextDate}. ¿Confirmar?`;

    return { amount, zeroChargeUsed, isZeroCharge, message };
  }

  private shouldBlockZeroChargeUpgrade(amount: number, zeroChargeUsed: boolean): boolean {
    return amount === 0 && zeroChargeUsed;
  }

  private configureUpgradeDialog(isZeroCharge: boolean): void {
    this.confirmDialogHeader = 'Actualizar plan';
    this.confirmDialogAcceptLabel = isZeroCharge ? 'Cambiar ahora' : 'Sí, actualizar';
    this.confirmDialogRejectLabel = 'Volver';
  }

  private executeUpgrade(planId: number, isZeroCharge: boolean): void {
    this.userService
      .upgrade(planId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showMessage(
            'success',
            'Plan actualizado',
            this.getUpgradeSuccessDetail(isZeroCharge)
          );
          this.refreshUserSubscriptionState();
          this.showPlansDialog = false;
          this.confirmationService.close();
        },
        error: (err) => {
          this.showMessage('error', 'Error al actualizar', this.getUpgradeErrorMessage(err));
          this.confirmationService.close();
        },
      });
  }

  private getUpgradeSuccessDetail(isZeroCharge: boolean): string {
    return isZeroCharge
      ? 'Plan actualizado sin cobro hoy. Tu próxima facturación aplicará el nuevo plan.'
      : 'Tu plan se ha actualizado correctamente.';
  }

  private refreshUserSubscriptionState(): void {
    this.userService
      .getUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.userService.setUser(user);
        this.user = user;
      });
    this.workspaceService.getCurrentWorkspace().pipe(takeUntil(this.destroy$)).subscribe();
  }

  private closeConfirmationOnReject(type: ConfirmEventType): void {
    if (type === ConfirmEventType.REJECT || type === ConfirmEventType.CANCEL) {
      this.confirmationService.close();
    }
  }

  private handleUpgradePreviewError(err: any): void {
    const backendMessage =
      err?.error?.message ??
      err?.error?.detail ??
      'No se pudo calcular el monto. Verifica que el plan sea superior al actual.';
    this.showMessage('error', 'Error al obtener vista previa', backendMessage);
  }

  private getUpgradeErrorMessage(err: any): string {
    const status = err?.status ?? err?.error?.code;
    const isMissingPaymentMethod =
      (status === 400 || status === '400') &&
      (err?.error?.message === 'Payment method not found' || err?.error?.code === 'PMT_001');

    if (isMissingPaymentMethod) {
      return 'No tienes un método de pago activo.';
    }

    if (Number(status) >= 500) {
      return 'Error al procesar tu pago';
    }

    return (
      err?.error?.message ?? err?.error?.detail ?? 'No se pudo cobrar. Revisa tu método de pago.'
    );
  }

  private formatDateOrEmpty(dateValue?: string): string {
    return dateValue
      ? new Date(dateValue).toLocaleDateString('es-CL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : '';
  }

  onReactivateClick(planId: number) {
    const planName = this.getPlanNameById(planId);
    const paymentLabel = this.paymentMethodDisplay ?? 'tu método de pago guardado';
    this.confirmDialogHeader = 'Reactivar suscripción';
    this.confirmDialogAcceptLabel = 'Sí, reactivar';
    this.confirmDialogRejectLabel = 'Volver';
    this.confirmationService.confirm({
      message: `Cobraremos el importe completo del plan <strong>${planName}</strong> usando ${paymentLabel} y activaremos un nuevo periodo desde hoy. ¿Confirmar?`,
      icon: 'pi pi-credit-card',
      accept: () => {
        this.userService
          .reactivate(planId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.showMessage(
                'success',
                'Suscripción reactivada',
                `Tu suscripción se ha activado nuevamente con el plan ${planName}.`
              );
              this.userService
                .getUser()
                .pipe(takeUntil(this.destroy$))
                .subscribe((user) => {
                  this.userService.setUser(user);
                  this.user = user;
                });
              this.showPlansDialog = false;
              this.confirmationService.close();
            },
            error: (err) => {
              const status = err?.status ?? err?.error?.code;
              let backendMessage =
                err?.error?.message ??
                err?.error?.detail ??
                'No se pudo cobrar. Revisa tu método de pago.';

              if (
                (status === 400 || status === '400') &&
                (backendMessage === 'Payment method not found' || err?.error?.code === 'PMT_001')
              ) {
                backendMessage =
                  'No tienes un método de pago activo. Regístralo para poder reactivar tu suscripción.';
              } else if (Number(status) >= 500) {
                backendMessage = 'Error al procesar tu pago';
              }

              this.showMessage('error', 'Error al reactivar', backendMessage);
              this.confirmationService.close();
            },
          });
      },
      reject: (type: ConfirmEventType) => {
        if (type === ConfirmEventType.REJECT || type === ConfirmEventType.CANCEL) {
          this.confirmationService.close();
        }
      },
    });
  }

  revertScheduledDowngrade() {
    this.userService
      .revertScheduledDowngrade()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showMessage(
            'success',
            'Cambio revertido',
            'El cambio de plan programado se ha revertido.'
          );
          this.userService
            .getUser()
            .pipe(takeUntil(this.destroy$))
            .subscribe((user) => {
              this.userService.setUser(user);
              this.user = user;
            });
        },
        error: () => {
          this.showMessage(
            'error',
            'Error al revertir',
            'No se pudo revertir el cambio de plan. Inténtalo de nuevo.'
          );
        },
      });
  }

  onDowngradeClick(planId: number) {
    const endDateRaw = this.user?.latest_active_subscription?.end_date;
    const endDateFormatted = endDateRaw
      ? new Date(endDateRaw).toLocaleDateString('es-CL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : 'fin del periodo';
    const planName = this.getPlanNameById(planId);
    const message = `Tu plan cambiará a ${planName} al final del periodo actual (${endDateFormatted}). Hasta entonces seguirás con tu plan actual. ¿Continuar?`;
    this.confirmDialogHeader = 'Bajar de plan';
    this.confirmDialogAcceptLabel = 'Continuar';
    this.confirmDialogRejectLabel = 'Volver';
    this.confirmationService.confirm({
      message,
      icon: 'pi pi-arrow-down',
      accept: () => {
        this.userService
          .scheduleDowngrade(planId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              const date = res?.effective_date
                ? new Date(res.effective_date).toLocaleDateString('es-CL', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })
                : endDateFormatted;
              this.showMessage(
                'success',
                'Cambio programado',
                `Cambiarás a ${res?.new_plan_name || planName} el ${date}.`
              );
              this.userService
                .getUser()
                .pipe(takeUntil(this.destroy$))
                .subscribe((user) => {
                  this.userService.setUser(user);
                  this.user = user;
                });
              this.showPlansDialog = false;
              this.confirmationService.close();
            },
            error: () => {
              this.showMessage(
                'error',
                'Error al programar',
                'No se pudo programar el cambio de plan. Inténtalo de nuevo.'
              );
              this.confirmationService.close();
            },
          });
      },
      reject: (type: ConfirmEventType) => {
        if (type === ConfirmEventType.REJECT || type === ConfirmEventType.CANCEL) {
          this.confirmationService.close();
        }
      },
    });
  }

  private getPlanNameById(planId: number): string {
    const names: Record<number, string> = { 3: 'Standard', 4: 'Premium' };
    return names[planId] || 'el plan elegido';
  }

  cancelSubscription() {
    const endDateRaw = this.user?.latest_active_subscription?.end_date;
    const endDateFormatted = endDateRaw
      ? new Date(endDateRaw).toLocaleDateString('es-CL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : 'la fecha límite';

    this.confirmDialogHeader = 'Finalizar suscripción';
    this.confirmDialogAcceptLabel = 'Sí, finalizar';
    this.confirmDialogRejectLabel = 'Volver';
    this.confirmationService.confirm({
      message: `¿En serio quieres finalizar tu suscripción? No podrás utilizar las funcionalidades que tienes acceso actualmente.<br><br>Tendrás acceso hasta el <strong>${endDateFormatted}</strong>. No se realizarán más cobros.`,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.userService
          .cancelSubscription()
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response: { end_date?: string }) => {
              const responseEndDate = response?.end_date;
              const confirmedDate = responseEndDate
                ? new Date(responseEndDate).toLocaleDateString('es-CL', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })
                : endDateFormatted;
              this.showMessage(
                'success',
                'Plan finalizado exitosamente',
                `Tu suscripción se mantendrá activa hasta el ${confirmedDate}.`
              );
              this.userService
                .getUser()
                .pipe(takeUntil(this.destroy$))
                .subscribe((user) => {
                  this.userService.setUser(user);
                  this.user = user;
                });
              this.confirmationService.close();
            },
            error: () => {
              this.showMessage(
                'error',
                'Error al finalizar suscripción',
                'Ha ocurrido un error. Por favor, inténtelo de nuevo más tarde.'
              );
              this.confirmationService.close();
            },
          });
      },
      reject: (type: ConfirmEventType) => {
        switch (type) {
          case ConfirmEventType.REJECT:
          case ConfirmEventType.CANCEL:
            this.confirmationService.close();
        }
      },
    });
  }
  showMessage(severity: string, summary: string, detail: string) {
    this.ms.add({ key: 'tst', severity, summary, detail });
  }
}
